import * as React from "react";
import {
  Disclosure as DisclosurePrimitive,
  DisclosurePanel as DisclosurePanelPrimitive,
  Button,
  Heading,
} from "react-aria-components";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A collapsible section (WAI-ARIA disclosure) over react-aria-components — a labelled trigger button that toggles a
 * panel. Used for progressive detail (e.g. "Attached to N facilities" in the error dialog). Keyboard + SR handled by
 * react-aria; token-styled. `title` is the always-visible trigger label; `children` is the collapsible panel.
 */
function Disclosure({
  title,
  children,
  defaultExpanded,
  isExpanded,
  onExpandedChange,
  headingLevel,
  className,
}: {
  title: React.ReactNode;
  /** The trigger's heading level in the page outline; react-aria's default is 3. */
  headingLevel?: number;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  /** CONTROLLED open state (ADR-027 §11: an "expand all" toggle + a remembered state drive the sections). Omit for
   *  the uncontrolled `defaultExpanded` behaviour. */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
}) {
  return (
    // overflow-hidden clips the trigger's corners to the container radius, so the (inset) focus ring + the divider
    // never poke past the single border — the earlier design doubled the border by ringing the button OUTSIDE it.
    <DisclosurePrimitive
      defaultExpanded={defaultExpanded}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      className={cn("border-border overflow-hidden rounded-md border", className)}
    >
      {({ isExpanded }) => (
        <>
          <Heading level={headingLevel} className="text-sm">
            <Button
              slot="trigger"
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left font-medium outline-none transition-colors",
                "hover:bg-muted/50 focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:ring-inset",
                isExpanded && "border-border bg-muted/30 border-b", // shading + header/content divider when open
              )}
            >
              <ChevronRight
                aria-hidden="true"
                className={cn("text-muted-foreground size-4 shrink-0 transition-transform", isExpanded && "rotate-90")}
              />
              <span className="flex-1">{title}</span>
            </Button>
          </Heading>
          {/* The divider is on the TRIGGER (border-b when open), NOT the panel: react-aria collapses the panel with
              hidden="until-found" → content-visibility:hidden, NOT display:none, so the panel box stays in flow and a
              border on it paints a 1px phantom line under the header even when collapsed. The trigger is always in
              flow, so its border is safe. (border-0 comes from the global reset; the border-b utility outranks it.) */}
          <DisclosurePanelPrimitive>{children}</DisclosurePanelPrimitive>
        </>
      )}
    </DisclosurePrimitive>
  );
}

export { Disclosure };
